import { seoPack } from "./seo-content";
import type { Locale } from "@/i18n/locales";
import {
  FAQ,
  GLOSSARY,
  HOW_IT_WORKS_INTRO,
  RANKING_RULES,
  SERVICES,
  STEPS,
  type ServiceIcon,
} from "./how-it-works-content";

export type HowItWorksContent = {
  intro: string;
  steps: { title: string; body: string }[];
  rankingRules: string[];
  glossary: [string, string][];
  faq: [string, string][];
  services: { key: ServiceIcon; title: string; body: string }[];
};

/** Localised text only: keys, links, icons and ordering always come from the English source. */
export type HowItWorksTranslation = Partial<Omit<HowItWorksContent, "services">> & {
  services?: { title: string; body: string }[];
};

const EN: HowItWorksContent = {
  intro: HOW_IT_WORKS_INTRO,
  steps: STEPS,
  rankingRules: RANKING_RULES,
  glossary: GLOSSARY,
  faq: FAQ,
  services: SERVICES.map((s) => ({ key: s.key, title: s.title, body: s.body })),
};


export function getHowItWorksContent(locale: Locale): HowItWorksContent {
  if (locale === "en") return EN;
  const t = seoPack(locale)?.howItWorks;
  if (!t) return EN;
  return {
    intro: t.intro ?? EN.intro,
    steps: t.steps?.length === EN.steps.length ? t.steps : EN.steps,
    rankingRules: t.rankingRules?.length ? t.rankingRules : EN.rankingRules,
    glossary: t.glossary?.length === EN.glossary.length ? t.glossary : EN.glossary,
    faq: t.faq?.length === EN.faq.length ? t.faq : EN.faq,
    services:
      t.services?.length === EN.services.length
        ? EN.services.map((s, i) => ({ ...s, ...t.services![i], key: s.key }))
        : EN.services,
  };
}

/** Service links/soon flags keyed by service id, so translations never touch routing. */
export const SERVICE_META = Object.fromEntries(
  SERVICES.map((s) => [s.key, { to: s.to, soon: s.soon }]),
) as Record<ServiceIcon, { to?: string; soon?: boolean }>;
